// frontend/src/pages/Auth/PendingApproval.jsx - VERSIÓN MINIMALISTA

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Progress,
} from 'reactstrap';

// ✅ Importar el logo
import masLogo from '../../assets/img/maslogosinletras.png';

const PendingApproval = () => {
  const { user, logout, refreshProfile, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [countdown, setCountdown] = useState(10);

  // Verificar cada 10 segundos si fue aprobado
  useEffect(() => {
    let countdownInterval;
    let checkInterval;

    const checkApproval = async () => {
      try {
        setChecking(true);
        const updatedUser = await refreshProfile();
        
        if (updatedUser.is_approved || updatedUser.role === 'super_admin') {
          navigate('/admin/dashboard', { replace: true });
        } else {
          setCountdown(10);
        }
      } catch (error) {
        console.error('Error verificando aprobación:', error);
      } finally {
        setChecking(false);
      }
    };

    countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return 10;
        }
        return prev - 1;
      });
    }, 1000);

    checkInterval = setInterval(checkApproval, 10000);

    return () => {
      clearInterval(countdownInterval);
      clearInterval(checkInterval);
    };
  }, [refreshProfile, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.is_approved) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleCheckNow = async () => {
    try {
      setChecking(true);
      const updatedUser = await refreshProfile();
      
      if (updatedUser.is_approved || updatedUser.role === 'super_admin') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        alert('Tu cuenta aún está pendiente de aprobación. Te notificaremos cuando sea aprobada.');
      }
    } catch (error) {
      console.error('Error verificando estado:', error);
      alert('Error al verificar el estado de tu cuenta');
    } finally {
      setChecking(false);
    }
  };

  const progressPercentage = ((10 - countdown) / 10) * 100;

  return (
    <div 
      className="min-vh-100 d-flex align-items-center position-relative overflow-hidden" 
      style={{ 
        background: 'linear-gradient(135deg, #038D81 0%, #05C9B4 100%)'
      }}
    >
      {/* ✅ Decoración de fondo - Igual que Login */}
      <div 
        className="position-absolute w-100 h-100" 
        style={{ 
          background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }}
      />

      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col lg="5" md="7">
            <Card className="border-0 mb-0 shadow-lg">
              <CardBody className="px-lg-5 py-lg-5">
                
                {/* ✅ Header con Logo MAS+ - Igual que Login */}
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <img 
                      src={masLogo} 
                      alt="MAS+ Logo" 
                      style={{ 
                        height: '80px',
                        width: 'auto',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))'
                      }}
                    />
                  </div>
                  <h3 className="text-dark font-weight-bold">Consultoría MÁS+</h3>
                  <p className="text-muted mb-0">Sistema de Gestión Empresarial</p>
                </div>

                {/* Usuario Info */}
                <div className="text-center mb-4 pb-3 border-bottom">
                  <div 
                    className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                    style={{ 
                      width: '70px', 
                      height: '70px',
                      background: 'linear-gradient(135deg, #038D81 0%, #05C9B4 100%)'
                    }}
                  >
                    {user?.profile_picture ? (
                      <img 
                        src={user.profile_picture} 
                        alt={user.first_name}
                        className="rounded-circle"
                        style={{ width: '66px', height: '66px', objectFit: 'cover' }}
                      />
                    ) : (
                      <span className="text-white font-weight-bold" style={{ fontSize: '1.5rem' }}>
                        {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                      </span>
                    )}
                  </div>
                  <h5 className="mb-1 text-dark font-weight-bold">
                    ¡Hola, {user?.first_name}!
                  </h5>
                  <small className="text-muted">{user?.email}</small>
                </div>

                {/* Estado Badge */}
                <div className="text-center mb-4">
                  <span 
                    className="badge badge-lg px-4 py-2"
                    style={{ 
                      background: 'linear-gradient(135deg, #038D81 0%, #05C9B4 100%)',
                      color: 'white',
                      fontSize: '0.85rem'
                    }}
                  >
                    <i className="ni ni-time-alarm me-2"></i>
                    Pendiente de Aprobación
                  </span>
                </div>

                {/* Mensaje */}
                <div 
                  className="p-3 rounded mb-4"
                  style={{ backgroundColor: 'rgba(3, 141, 129, 0.08)' }}
                >
                  <div className="d-flex align-items-start">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                      style={{ 
                        width: '40px', 
                        height: '40px',
                        background: 'linear-gradient(135deg, #038D81 0%, #05C9B4 100%)'
                      }}
                    >
                      <i className="ni ni-bell-55 text-white"></i>
                    </div>
                    <div>
                      <h6 className="mb-1 text-dark font-weight-bold">Tu solicitud está siendo revisada</h6>
                      <p className="text-muted mb-0 text-sm">
                        Un administrador aprobará tu acceso pronto. Te notificaremos por correo electrónico.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Timeline Simple */}
                <div className="mb-4">
                  <Row className="text-center">
                    <Col xs="4">
                      <div 
                        className="mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '45px', 
                          height: '45px',
                          background: 'linear-gradient(135deg, #2dce89 0%, #26a65b 100%)'
                        }}
                      >
                        <i className="ni ni-check-bold text-white"></i>
                      </div>
                      <small className="text-dark font-weight-bold d-block">Cuenta creada</small>
                      <small className="text-success">Completado</small>
                    </Col>
                    <Col xs="4">
                      <div 
                        className="mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '45px', 
                          height: '45px',
                          background: 'linear-gradient(135deg, #038D81 0%, #05C9B4 100%)',
                          animation: 'pulse 2s infinite'
                        }}
                      >
                        <i className="ni ni-time-alarm text-white"></i>
                      </div>
                      <small className="text-dark font-weight-bold d-block">En revisión</small>
                      <small className="text-primary">En proceso</small>
                    </Col>
                    <Col xs="4">
                      <div 
                        className="mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '45px', 
                          height: '45px',
                          backgroundColor: '#e9ecef'
                        }}
                      >
                        <i className="ni ni-email-83 text-muted"></i>
                      </div>
                      <small className="text-dark font-weight-bold d-block">Notificación</small>
                      <small className="text-muted">Pendiente</small>
                    </Col>
                  </Row>
                </div>

                {/* Progress Timer */}
                <div className="mb-4 p-3 bg-light rounded">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">
                      <i className="ni ni-watch-time me-1"></i>
                      Próxima verificación automática
                    </small>
                    <small className="font-weight-bold" style={{ color: '#038D81' }}>{countdown}s</small>
                  </div>
                  <Progress 
                    value={progressPercentage} 
                    style={{ height: '6px' }}
                    color="primary"
                  />
                </div>

                {/* Botón Verificar */}
                <Button
                  color="primary"
                  size="lg"
                  block
                  onClick={handleCheckNow}
                  disabled={checking}
                  className="shadow-lg mb-3"
                >
                  {checking ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Verificando...
                    </>
                  ) : (
                    <>
                      <i className="ni ni-curved-next me-2"></i>
                      Verificar Estado Ahora
                    </>
                  )}
                </Button>

                {/* Botón Cerrar Sesión */}
                <Button
                  color="light"
                  size="lg"
                  block
                  onClick={logout}
                  className="border"
                >
                  <i className="ni ni-user-run me-2"></i>
                  Cerrar Sesión
                </Button>

                {/* Footer Info */}
                <div className="text-center mt-4 pt-3 border-top">
                  <small className="text-muted">
                    <i className="ni ni-support-16 me-1"></i>
                    ¿Necesitas ayuda? Contacta al administrador del sistema
                  </small>
                </div>

              </CardBody>
            </Card>

            {/* Copyright - Igual que Login */}
            <div className="text-center mt-4">
              <p className="text-white text-sm mb-0 font-weight-bold">
                © 2024 Consultoría MAS+. Todos los derechos reservados.
              </p>
            </div>
          </Col>
        </Row>
      </Container>

      {/* CSS para animación pulse */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(3, 141, 129, 0.4);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 10px rgba(3, 141, 129, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default PendingApproval;
